import type { DefaultValues } from 'react-hook-form';

import type {
  FinalSettlementSubmitValues,
  PaymentTranche,
} from './final-settlement-invoice-schema';
import type { BlContextForTradeInvoice } from './trade-invoice-payload';

// The receivable Final Invoice auto-fills its number (Launchpad parity: the
// field is hidden). The chain-aware server suggestion (FS-2026-01700-02) is
// the value; the deterministic per-BL legacy string only covers the window
// before the suggestion resolves — the field is invisible on the sell side,
// so an empty value would silently block submit.
function sellFinalInvoiceNumber(bl: BlContextForTradeInvoice): string {
  const suffix = bl.blOrderName ?? bl.contractNumber ?? 'INVOICE';
  return `FINAL-${suffix}`;
}

const DEFAULT_SELL_FINAL_TRANCHE: PaymentTranche = { percentage: 100, trigger: 'ATA at PoD' };

// Sell-side Final create overlay: hides the trade-form fields behind sensible
// auto-values (number, single 100% tranche) so the reshaped dialog only has to
// surface Quantity / Due Date / Remarks + the schedule editor.
export function applySellFinalCreateDefaults(
  base: DefaultValues<FinalSettlementSubmitValues>,
  bl: BlContextForTradeInvoice,
  suggestedNumber: string | null,
): DefaultValues<FinalSettlementSubmitValues> {
  return {
    ...base,
    invoice_number: suggestedNumber ?? sellFinalInvoiceNumber(bl),
    payment_schedule: [DEFAULT_SELL_FINAL_TRANCHE],
  };
}

// Maps the editor's tranches to the backend payload shape. Only Fixed-Date
// tranches carry an explicit `due_date`; every other trigger resolves its date
// downstream from the shipment milestone.
export function toWireSchedule(
  tranches: readonly PaymentTranche[] | undefined,
): { percentage: number; payment_trigger: string; due_date: string | null }[] {
  if (tranches === undefined) return [];
  return tranches.map((t) => ({
    percentage: t.percentage,
    payment_trigger: t.trigger,
    due_date: t.trigger === 'Fixed Date' ? (t.fixedDate ?? null) : null,
  }));
}
